import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, Upload, Calendar, Image, Loader, AlertTriangle, X } from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)', surf3: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', bord2: 'var(--nd-w10)',
  text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', red: '#F87171', green: '#34D399',
  amber: '#FBBF24', font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes modalIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }

  .cjom-date {
    width:100%; background:var(--nd-w05); border:1px solid var(--nd-w10);
    border-radius:9px; color:var(--nd-text); font-size:13px; font-family:'DM Sans',sans-serif;
    padding:9px 12px 9px 34px; height:40px; outline:none;
    transition:border-color 0.15s,background 0.15s; color-scheme:dark;
  }
  .cjom-date:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }
  .cjom-label { display:block; font-size:10px; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--nd-tx40); margin-bottom:5px; }

  .cjom-file-zone {
    width:100%; padding:11px 14px; border-radius:9px;
    border:1.5px dashed var(--nd-w15); background:var(--nd-w03);
    cursor:pointer; transition:all 0.15s; display:flex; align-items:center; gap:9px;
    color:var(--nd-tx30); font-size:12px; font-family:'DM Sans',sans-serif; position:relative;
  }
  .cjom-file-zone:hover { border-color:rgba(59,130,246,0.45); background:rgba(59,130,246,0.04); color:#60A5FA; }
  .cjom-file-input { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }

  .cjom-pill { display:inline-block; padding:2px 8px; border-radius:4px; font-size:10px; font-family:'DM Mono',monospace; }

  .cjom-btn-submit { display:inline-flex; align-items:center; gap:7px; padding:9px 20px; border-radius:9px; border:none; background:linear-gradient(135deg,#22D3A5,#3B82F6); color:var(--nd-text-strong); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; cursor:pointer; box-shadow:0 4px 14px rgba(34,211,165,0.22); transition:opacity 0.15s; }
  .cjom-btn-submit:hover:not(:disabled) { opacity:.88; }
  .cjom-btn-submit:disabled { opacity:.45; cursor:not-allowed; }
  .cjom-btn-cancel { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; border-radius:9px; border:1px solid var(--nd-w10); background:var(--nd-w05); color:var(--nd-tx45); font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .cjom-btn-cancel:hover { background:var(--nd-w08); color:var(--nd-tx70); }
`;

const Field = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label className="cjom-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
const CompleteJobOrderModal = ({ show, onHide, jobOrder, onJobUpdated }) => {
  const [actualDate, setActualDate]     = useState('');
  const [fsrFile, setFsrFile]           = useState(null);
  const [beforeImages, setBeforeImages] = useState([]);
  const [afterImages, setAfterImages]   = useState([]);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState('');

  console.log(jobOrder);

  const handleFileChange = (e, setter, limit) => {
    const files = Array.from(e.target.files);
    if (files.length > limit) { alert(`You can only upload up to ${limit} images.`); return; }
    setter(files);
  };

  const handleSubmit = async () => {
    if (!actualDate || !fsrFile || beforeImages.length === 0 || afterImages.length === 0) {
      alert('Please fill all required fields and upload all images.'); return;
    }
    const formData = new FormData();
    formData.append('actualDate', actualDate);
    formData.append('fsr', fsrFile);
    beforeImages.forEach(img => formData.append('beforeImages', img));
    afterImages.forEach(img => formData.append('afterImages', img));
    try {
      setSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/job-orders/${jobOrder.job_id}/complete`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onJobUpdated(); onHide();
    } catch (err) {
      console.error(err); setError('Failed to mark job order as complete.');
    } finally { setSubmitting(false); }
  };

  if (!show) return null;

  return (
    <>
      <style>{STYLES}</style>
      {/* Overlay */}
      <div
        style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',backdropFilter:'blur(4px)',zIndex:1050,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}
        onClick={onHide}
      >
        {/* Panel */}
        <div
          style={{ background:T.surface,border:`1px solid ${T.bord2}`,borderRadius:16,width:'100%',maxWidth:520,maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 60px rgba(0,0,0,0.7)',animation:'modalIn 0.2s ease',fontFamily:T.font }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ padding:'16px 20px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0 }}>
            <div style={{ display:'flex',alignItems:'center',gap:9 }}>
              <div style={{ width:28,height:28,borderRadius:8,background:'rgba(52,211,153,0.12)',border:'1px solid rgba(52,211,153,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <CheckCircle size={14} color={T.green} />
              </div>
              <div>
                <div style={{ fontSize:14,fontWeight:700,color:T.text }}>Complete Job Order</div>
                <div style={{ fontSize:10,color:T.muted,fontFamily:T.mono }}>#{jobOrder?.job_id}</div>
              </div>
            </div>
            <button onClick={onHide} style={{ background:'var(--nd-w06)',border:'1px solid var(--nd-w10)',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:T.muted }}>
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding:'16px 20px',overflowY:'auto',flex:1 }}>
            {/* Error */}
            {error && (
              <div style={{ background:'rgba(248,113,113,0.10)',border:'1px solid rgba(248,113,113,0.28)',borderRadius:9,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:8,color:T.red,fontSize:12 }}>
                <AlertTriangle size={13} /> {error}
              </div>
            )}

            {/* Info strip */}
            <div style={{ background:T.surf2,border:`1px solid ${T.border}`,borderRadius:10,padding:'12px 14px',marginBottom:16,display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:'8px 16px' }}>
              {[
                { label:'Client',    value: jobOrder?.client_name },
                { label:'Address',   value: jobOrder?.address },
                { label:'Scheduled', value: jobOrder?.scheduled_date ? new Date(jobOrder.scheduled_date).toLocaleDateString('en-GB') : '—' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize:9,color:T.muted,letterSpacing:'0.10em',textTransform:'uppercase',marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:12,color:T.text,fontWeight:500 }}>{value || '—'}</div>
                </div>
              ))}
            </div>

            {/* Actual date */}
            <Field label="Actual Cleaning Date" icon={Calendar} iconColor={T.blue}>
              <input className="cjom-date" type="date" value={actualDate} onChange={e => setActualDate(e.target.value)} />
            </Field>

            {/* FSR */}
            <div style={{ marginBottom: 14 }}>
              <label className="cjom-label">FSR Upload</label>
              <div className="cjom-file-zone">
                <input type="file" className="cjom-file-input" accept="image/*" onChange={e => setFsrFile(e.target.files[0])} />
                <Upload size={13} color={T.teal} />
                <span>{fsrFile ? fsrFile.name : 'Click to upload FSR image…'}</span>
              </div>
            </div>

            {/* Before images */}
            <div style={{ marginBottom: 14 }}>
              <label className="cjom-label">
                Before Pictures&nbsp;
                <span style={{ color:T.muted,fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9 }}>(max 7)</span>
              </label>
              <div className="cjom-file-zone">
                <input type="file" className="cjom-file-input" accept="image/*" multiple onChange={e => handleFileChange(e, setBeforeImages, 7)} />
                <Image size={13} color={T.amber} />
                <span>{beforeImages.length > 0 ? `${beforeImages.length} file(s) selected` : 'Click to upload before photos…'}</span>
              </div>
              {beforeImages.length > 0 && (
                <div style={{ display:'flex',flexWrap:'wrap',gap:5,marginTop:6 }}>
                  {beforeImages.map((f,i) => (
                    <span key={i} className="cjom-pill" style={{ background:'rgba(251,191,36,0.10)',border:'1px solid rgba(251,191,36,0.22)',color:T.amber }}>{f.name}</span>
                  ))}
                </div>
              )}
            </div>

            {/* After images */}
            <div style={{ marginBottom: 4 }}>
              <label className="cjom-label">
                After Pictures&nbsp;
                <span style={{ color:T.muted,fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9 }}>(max 7)</span>
              </label>
              <div className="cjom-file-zone">
                <input type="file" className="cjom-file-input" accept="image/*" multiple onChange={e => handleFileChange(e, setAfterImages, 7)} />
                <Image size={13} color={T.green} />
                <span>{afterImages.length > 0 ? `${afterImages.length} file(s) selected` : 'Click to upload after photos…'}</span>
              </div>
              {afterImages.length > 0 && (
                <div style={{ display:'flex',flexWrap:'wrap',gap:5,marginTop:6 }}>
                  {afterImages.map((f,i) => (
                    <span key={i} className="cjom-pill" style={{ background:'rgba(52,211,153,0.10)',border:'1px solid rgba(52,211,153,0.22)',color:T.green }}>{f.name}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding:'12px 20px 16px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:8,flexShrink:0 }}>
            <button className="cjom-btn-cancel" onClick={onHide} disabled={submitting}>Cancel</button>
            <button className="cjom-btn-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting
                ? <><Loader size={13} style={{ animation:'spin 0.8s linear infinite' }} /> Completing…</>
                : <><CheckCircle size={13} /> Mark as Complete</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompleteJobOrderModal;